import {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  type MutableRefObject,
} from 'react';

import { useOnlineOrdersStorage } from '../../storage';
import { useProcessedMenus } from '../../hooks/useProcessedMenus';
import {
  buildSectionTabs,
  buildMenuOptions,
} from '../../../../utils/menuNavigation';
import { MENU_QUERY_PARAMS } from '../../../../utils/constants';

const SCROLL_LOCK_DURATION_MS = 1000;
const OBSERVER_TOP_MARGIN = '-10%';
const OBSERVER_BOTTOM_MARGIN = '-80%';
const SECTION_SELECTOR = '[data-section-id]';

export const useHooks = () => {
  const { cartQuantities, openCart, isPremium } = useOnlineOrdersStorage();
  const { processedMenus: menus } = useProcessedMenus();

  const hasMultipleMenus = menus.length > 1;

  const menuOptions = useMemo(() => buildMenuOptions(menus), [menus]);

  const [selectedMenuId, setSelectedMenuId] = useState('');

  const activeMenuId = selectedMenuId || menus[0]?.id;

  const sectionTabs = useMemo(
    () => buildSectionTabs(menus, activeMenuId),
    [menus, activeMenuId],
  );

  const [activeSectionId, setActiveSectionId] = useState('');

  const activeTabId = activeSectionId || sectionTabs[0]?.id;

  const isScrollingToSectionRef = useRef(false);

  useScrollToMenuOrMenuSection({
    menus,
    sectionTabs,
    isScrollingToSectionRef,
    setSelectedMenuId,
    setActiveSectionId,
  });

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>(SECTION_SELECTOR);

    if (elements.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingToSectionRef.current) {
          return;
        }

        for (const entry of entries) {
          if (entry.isIntersecting) {
            const sectionId = (entry.target as HTMLElement).dataset.sectionId;

            if (sectionId) {
              setActiveSectionId(sectionId);
            }
          }
        }
      },
      {
        rootMargin: `${OBSERVER_TOP_MARGIN} 0px ${OBSERVER_BOTTOM_MARGIN} 0px`,
        threshold: 0,
      },
    );

    for (const el of elements) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sectionTabs]);

  const onSectionTabClick = useCallback((sectionId: string) => {
    setActiveSectionId(sectionId);
    isScrollingToSectionRef.current = true;

    const sectionElement = document.querySelector(
      `[data-section-id="${sectionId}"]`,
    );

    sectionElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    setTimeout(() => {
      isScrollingToSectionRef.current = false;
    }, SCROLL_LOCK_DURATION_MS);
  }, []);

  const onMenuChange = useCallback((menuId: string) => {
    setSelectedMenuId(menuId);
    setActiveSectionId('');

    isScrollingToSectionRef.current = true;

    const menuElement = document.querySelector(`[data-menu-id="${menuId}"]`);

    menuElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    setTimeout(() => {
      isScrollingToSectionRef.current = false;
    }, SCROLL_LOCK_DURATION_MS);
  }, []);

  const cartCount = Object.values(cartQuantities).reduce(
    (sum, qty) => sum + qty,
    0,
  );

  return {
    hasMultipleMenus,
    menuOptions,
    activeMenuId,
    onMenuChange,
    sectionTabs,
    activeTabId,
    onSectionTabClick,
    cartCount,
    openCart: () => openCart(),
    isPremium,
  };
};

type ScrollToMenuOrSectionParams = {
  menus: ReturnType<typeof useOnlineOrdersStorage>['menus'];
  sectionTabs: { id: string; label: string }[];
  isScrollingToSectionRef: MutableRefObject<boolean>;
  setSelectedMenuId: (id: string) => void;
  setActiveSectionId: (id: string) => void;
};

/**
 * On initial page load, scrolls to the menu/section specified in the URL params.
 * `?menuId=<id>` → scrolls to the menu.
 * `?sectionId=<id>&menuId=<id>` → scrolls to the section.
 */
const useScrollToMenuOrMenuSection = ({
  menus,
  sectionTabs,
  isScrollingToSectionRef,
  setSelectedMenuId,
  setActiveSectionId,
}: ScrollToMenuOrSectionParams) => {
  const hasInitialScrolledRef = useRef(false);

  useEffect(() => {
    if (hasInitialScrolledRef.current || sectionTabs.length === 0) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const itemId = params.get(MENU_QUERY_PARAMS.itemId);
    const sectionId = params.get(MENU_QUERY_PARAMS.sectionId);
    const menuId = params.get(MENU_QUERY_PARAMS.menuId);

    if (itemId) {
      hasInitialScrolledRef.current = true;

      return;
    }

    hasInitialScrolledRef.current = true;

    if (sectionId) {
      const parentMenu = menus.find((m) =>
        m.sections.some((s) => s.id === sectionId),
      );

      if (parentMenu) {
        setSelectedMenuId(parentMenu.id);
      }

      setActiveSectionId(sectionId);
      scrollToElement(
        document.querySelector(`[data-section-id="${sectionId}"]`),
      );
    } else if (menuId) {
      setSelectedMenuId(menuId);
      scrollToElement(document.querySelector(`[data-menu-id="${menuId}"]`));
    }

    function scrollToElement(element: Element | null) {
      isScrollingToSectionRef.current = true;
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        isScrollingToSectionRef.current = false;
      }, SCROLL_LOCK_DURATION_MS);
    }
  }, [
    sectionTabs,
    menus,
    isScrollingToSectionRef,
    setSelectedMenuId,
    setActiveSectionId,
  ]);
};
